/**
 * Partner Detail Page
 * View and manage a single partner with tabs for affiliates, contacts, and reporting
 */

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getPartner,
  getPartnerStats,
  deletePartner,
  togglePartnerActive,
  getPartnerApiKeys,
  createPartnerApiKey,
  revokeApiKey,
} from "../services/partners.service";
import {
  getAffiliates,
  deleteAffiliate,
  toggleAffiliateActive,
} from "../services/affiliates.service";
import Button from "../components/common/Button";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Modal from "../components/common/Modal";
import PartnerForm from "../components/partners/PartnerForm";
import AffiliateForm from "../components/partners/AffiliateForm";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ChartBarIcon,
  KeyIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import type { Affiliate } from "../types/partner.types";

type TabId = "overview" | "affiliates" | "contacts" | "api-keys" | "reporting";

interface Tab {
  id: TabId;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  count?: number;
}

const PartnerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Determine if we're in create mode (no id means /partners/new route)
  const isCreateMode = !id;

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAffiliateModalOpen, setIsAffiliateModalOpen] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState<Affiliate | null>(
    null,
  );
  const [newApiKeySecret, setNewApiKeySecret] = useState<string | null>(null);

  // Fetch partner (only when not in create mode)
  const {
    data: partner,
    isLoading: partnerLoading,
    error: partnerError,
  } = useQuery({
    queryKey: ["partner", id],
    queryFn: () => getPartner(id!),
    enabled: !isCreateMode,
  });

  // Fetch stats (only when not in create mode)
  const { data: stats } = useQuery({
    queryKey: ["partner-stats", id],
    queryFn: () => getPartnerStats(id!),
    enabled: !isCreateMode,
  });

  // Fetch affiliates (only when not in create mode)
  const { data: affiliatesData } = useQuery({
    queryKey: ["affiliates", id],
    queryFn: () => getAffiliates(id!, { page: 1, limit: 100 }),
    enabled: !isCreateMode,
  });

  // Fetch API keys (only when not in create mode)
  const { data: apiKeys } = useQuery({
    queryKey: ["partner-api-keys", id],
    queryFn: () => getPartnerApiKeys(id!),
    enabled: !isCreateMode,
  });

  const affiliates = affiliatesData?.data || [];

  // Mutations
  const toggleActiveMutation = useMutation({
    mutationFn: ({ isActive }: { isActive: boolean }) =>
      togglePartnerActive(id!, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner", id] });
      queryClient.invalidateQueries({ queryKey: ["affiliates", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deletePartner(id!),
    onSuccess: () => {
      navigate("/partners");
    },
  });

  const deleteAffiliateMutation = useMutation({
    mutationFn: (affiliateId: string) => deleteAffiliate(affiliateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates", id] });
      queryClient.invalidateQueries({ queryKey: ["partner-stats", id] });
    },
  });

  const toggleAffiliateMutation = useMutation({
    mutationFn: ({
      affiliateId,
      isActive,
    }: {
      affiliateId: string;
      isActive: boolean;
    }) => toggleAffiliateActive(affiliateId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["affiliates", id] });
      queryClient.invalidateQueries({ queryKey: ["partner-stats", id] });
    },
  });

  const createApiKeyMutation = useMutation({
    mutationFn: (name: string) =>
      createPartnerApiKey({ partner_id: id!, name }),
    onSuccess: (result) => {
      setNewApiKeySecret(result.secret);
      queryClient.invalidateQueries({ queryKey: ["partner-api-keys", id] });
    },
  });

  const revokeApiKeyMutation = useMutation({
    mutationFn: (keyId: string) => revokeApiKey(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["partner-api-keys", id] });
    },
  });

  // Define tabs
  const tabs: Tab[] = [
    { id: "overview", label: "Overview", icon: InformationCircleIcon },
    {
      id: "affiliates",
      label: "Affiliates",
      icon: BuildingStorefrontIcon,
      count: stats?.total_affiliates || 0,
    },
    {
      id: "contacts",
      label: "Contacts",
      icon: UserGroupIcon,
      count: stats?.total_contacts || 0,
    },
    {
      id: "api-keys",
      label: "API Keys",
      icon: KeyIcon,
      count: apiKeys?.length || 0,
    },
    { id: "reporting", label: "Reporting", icon: ChartBarIcon },
  ];

  // Show loading only when loading an existing partner
  if (!isCreateMode && partnerLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" text="Loading partner..." />
      </div>
    );
  }

  // Show error only when loading an existing partner fails
  if (!isCreateMode && (partnerError || !partner)) {
    return (
      <Card
        variant="outlined"
        padding="md"
        className="border-red-200 bg-red-50"
      >
        <p className="text-red-800">Partner not found or error loading data.</p>
        <Button
          variant="secondary"
          onClick={() => navigate("/partners")}
          className="mt-4"
        >
          Back to Partners
        </Button>
      </Card>
    );
  }

  // Create mode - show the create form
  if (isCreateMode) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/partners")}
            className="flex items-center gap-1"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Add New Partner
            </h1>
            <p className="text-gray-500 mt-1">
              Create a new partner organization
            </p>
          </div>
        </div>

        {/* Create Form */}
        <Card variant="outlined" padding="lg">
          <PartnerForm
            onSuccess={(newPartner) => {
              // Navigate to the new partner's detail page
              navigate(`/partners/${newPartner.id}`);
            }}
            onCancel={() => navigate("/partners")}
          />
        </Card>
      </div>
    );
  }

  const handleCreateApiKey = () => {
    const name = prompt("Enter a name for this API key:");
    if (name) {
      createApiKeyMutation.mutate(name);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate("/partners")}
            className="flex items-center gap-1"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">
                {partner.name}
              </h1>
              <Badge variant={partner.is_active ? "success" : "default"}>
                {partner.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
            {partner.title && (
              <p className="text-gray-500 mt-1">{partner.title}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={partner.is_active ? "secondary" : "primary"}
            size="sm"
            onClick={() =>
              toggleActiveMutation.mutate({ isActive: !partner.is_active })
            }
            disabled={toggleActiveMutation.isPending}
          >
            {partner.is_active ? (
              <>
                <XCircleIcon className="h-4 w-4 mr-1" />
                Deactivate
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                Activate
              </>
            )}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
          >
            <PencilIcon className="h-4 w-4 mr-1" />
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setIsDeleteModalOpen(true)}
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-primary-500 text-primary-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
                {tab.count !== undefined && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id
                        ? "bg-primary-100 text-primary-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Partner Info */}
          <Card variant="outlined" padding="md">
            <h3 className="font-semibold text-gray-900 mb-4">
              Partner Information
            </h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Name</dt>
                <dd className="text-gray-900">{partner.name}</dd>
              </div>
              {partner.title && (
                <div>
                  <dt className="text-sm text-gray-500">Title</dt>
                  <dd className="text-gray-900">{partner.title}</dd>
                </div>
              )}
              {partner.email && (
                <div>
                  <dt className="text-sm text-gray-500">Email</dt>
                  <dd className="text-gray-900">{partner.email}</dd>
                </div>
              )}
              {partner.phone && (
                <div>
                  <dt className="text-sm text-gray-500">Phone</dt>
                  <dd className="text-gray-900">{partner.phone}</dd>
                </div>
              )}
              {partner.website && (
                <div>
                  <dt className="text-sm text-gray-500">Website</dt>
                  <dd>
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {partner.website}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Address */}
          <Card variant="outlined" padding="md">
            <h3 className="font-semibold text-gray-900 mb-4">Address</h3>
            {partner.address_line1 ? (
              <address className="text-gray-900 not-italic">
                {partner.address_line1}
                {partner.address_line2 && (
                  <>
                    <br />
                    {partner.address_line2}
                  </>
                )}
                <br />
                {partner.city}, {partner.state} {partner.postal_code}
                <br />
                {partner.country}
              </address>
            ) : (
              <p className="text-gray-400">No address provided</p>
            )}
          </Card>

          {/* Stats */}
          <Card variant="outlined" padding="md" className="md:col-span-2">
            <h3 className="font-semibold text-gray-900 mb-4">Statistics</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {stats?.total_affiliates || 0}
                </div>
                <div className="text-sm text-gray-500">Total Affiliates</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {stats?.active_affiliates || 0}
                </div>
                <div className="text-sm text-gray-500">Active Affiliates</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {stats?.total_contacts || 0}
                </div>
                <div className="text-sm text-gray-500">Contacts</div>
              </div>
            </div>
          </Card>

          {/* Notes */}
          {partner.notes && (
            <Card variant="outlined" padding="md" className="md:col-span-2">
              <h3 className="font-semibold text-gray-900 mb-4">Notes</h3>
              <p className="text-gray-700 whitespace-pre-wrap">
                {partner.notes}
              </p>
            </Card>
          )}
        </div>
      )}

      {activeTab === "affiliates" && (
        <Card variant="outlined" padding="md">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Affiliates</h3>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditingAffiliate(null);
                setIsAffiliateModalOpen(true);
              }}
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Affiliate
            </Button>
          </div>

          {affiliates.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Affiliate #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {affiliates.map((affiliate) => (
                  <tr key={affiliate.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        {affiliate.affiliate_number}
                      </code>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {affiliate.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {affiliate.city && affiliate.state
                        ? `${affiliate.city}, ${affiliate.state}`
                        : "-"}
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        variant={affiliate.is_active ? "success" : "default"}
                      >
                        {affiliate.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          toggleAffiliateMutation.mutate({
                            affiliateId: affiliate.id,
                            isActive: !affiliate.is_active,
                          })
                        }
                      >
                        {affiliate.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setEditingAffiliate(affiliate);
                          setIsAffiliateModalOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => {
                          if (
                            confirm(
                              `Are you sure you want to delete affiliate "${affiliate.name}"?`,
                            )
                          ) {
                            deleteAffiliateMutation.mutate(affiliate.id);
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <BuildingStorefrontIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No affiliates yet.</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setEditingAffiliate(null);
                  setIsAffiliateModalOpen(true);
                }}
                className="mt-4"
              >
                Add First Affiliate
              </Button>
            </div>
          )}
        </Card>
      )}

      {activeTab === "contacts" && (
        <Card variant="outlined" padding="md">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Partner Contacts</h3>
            <Button variant="primary" size="sm">
              <PlusIcon className="h-4 w-4 mr-1" />
              Add Contact
            </Button>
          </div>
          <div className="text-center py-8">
            <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Contact management coming soon.</p>
            <p className="text-sm text-gray-400 mt-2">
              Contacts will be linked using the contact roles system.
            </p>
          </div>
        </Card>
      )}

      {activeTab === "api-keys" && (
        <Card variant="outlined" padding="md">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-900">API Keys</h3>
              <p className="text-sm text-gray-500 mt-1">
                API keys allow external systems to query affiliate data
              </p>
            </div>
            <Button variant="primary" size="sm" onClick={handleCreateApiKey}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Create API Key
            </Button>
          </div>

          {newApiKeySecret && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-yellow-800">
                    Save your API key now!
                  </p>
                  <p className="text-sm text-yellow-700 mt-1">
                    This is the only time you'll see this key. Copy it somewhere
                    safe.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono">
                      {newApiKeySecret}
                    </code>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        copyToClipboard(newApiKeySecret);
                        alert("Copied to clipboard!");
                      }}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setNewApiKeySecret(null)}
                    className="mt-3"
                  >
                    I've saved it
                  </Button>
                </div>
              </div>
            </div>
          )}

          {apiKeys && apiKeys.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Key Prefix
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Used
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {key.name}
                    </td>
                    <td className="px-4 py-3">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                        {key.key_prefix}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={key.is_active ? "success" : "error"}>
                        {key.is_active ? "Active" : "Revoked"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {key.last_used_at
                        ? new Date(key.last_used_at).toLocaleDateString()
                        : "Never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {key.is_active && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => {
                            if (
                              confirm(
                                `Are you sure you want to revoke API key "${key.name}"?`,
                              )
                            ) {
                              revokeApiKeyMutation.mutate(key.id);
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-8">
              <KeyIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No API keys yet.</p>
              <p className="text-sm text-gray-400 mt-2">
                Create an API key to allow external access to affiliate data.
              </p>
            </div>
          )}
        </Card>
      )}

      {activeTab === "reporting" && (
        <Card variant="outlined" padding="md">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">
              Performance Reporting
            </h3>
          </div>
          <div className="text-center py-12">
            <ChartBarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Coming Soon</p>
            <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
              Affiliate performance reporting will be available once Zuora
              subscription source data integration is complete. This will show
              subscription metrics per affiliate.
            </p>
          </div>
        </Card>
      )}

      {/* Edit Partner Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Partner"
        size="lg"
      >
        <PartnerForm
          partner={partner}
          onSuccess={() => {
            setIsEditModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ["partner", id] });
          }}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Partner"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{partner.name}</strong>?
            This will hide the partner and all its affiliates from the system.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                deleteMutation.mutate();
                setIsDeleteModalOpen(false);
              }}
            >
              Delete Partner
            </Button>
          </div>
        </div>
      </Modal>

      {/* Affiliate Form Modal */}
      <Modal
        isOpen={isAffiliateModalOpen}
        onClose={() => {
          setIsAffiliateModalOpen(false);
          setEditingAffiliate(null);
        }}
        title={editingAffiliate ? "Edit Affiliate" : "Add Affiliate"}
        size="lg"
      >
        <AffiliateForm
          partnerId={id!}
          affiliate={editingAffiliate || undefined}
          onSuccess={() => {
            setIsAffiliateModalOpen(false);
            setEditingAffiliate(null);
            queryClient.invalidateQueries({ queryKey: ["affiliates", id] });
            queryClient.invalidateQueries({ queryKey: ["partner-stats", id] });
          }}
          onCancel={() => {
            setIsAffiliateModalOpen(false);
            setEditingAffiliate(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default PartnerDetail;
